import React, {useImperativeHandle, useState, forwardRef, useEffect} from "react";

import ToolTip from "@app/components/2_widgets/ToolTip";

/* Props - The following props must be indicated when calling the parent component
*  label        : string
*  inputId      : string, to identify input and link it to its label
*  placeholder  : string
*  type         : string, can be integer / string to apply validation controls
*  defaultValue : string, default input value
*  isRequired   : bool, set to true if the input is mandatory
*  isReadOnly   : bool, set to true if input shouldn't be modified
*/
const Input = forwardRef( function Input({ 
    label, inputId, placeholder, isRequired, isReadOnly=false, defaultValue="", 
    type="text", tooltip, error, callbackBlur, callbackOnChange
}, ref) {
    const defaultError = {error: false, message: ""};

    const [value, setValue] = useState(defaultValue);
    const [hasErrors, setHasErrors] = useState(defaultError);
    const [errorChecked, setErrorChecked] = useState(false);

    useImperativeHandle(ref, ()=>({
        getValue() {
            return value;
        },
        hasErrors() {
            return hasErrors.error;
        },
        checkErrors() {
            checkErrors();
        },
        setValue(val) {
            setValue(val);
        },
        resetValue() {
            setHasErrors(defaultError);
            setErrorChecked(false);
            setValue("");
        }
    }));

    useEffect(() => {        
        setValue(defaultValue);
    }, [defaultValue]);

    /**
     * Check if there is an error in this input
     */
    function checkErrors() {
        setErrorChecked(true);
        let isOnError = error.controlErrors(value);

        setHasErrors(isOnError);
        
        if (callbackBlur)
            callbackBlur(isOnError, value);
    }

    function displayErrorMessage() {        
        if (isRequired && hasErrors.error) {
            return (
                <span className="input-error-message">{hasErrors.message}</span>
            )
        }
    }

    function displayTooltip() {
        if (tooltip) {
            return (
                <div className="input-tooltip">
                    <ToolTip title={tooltip.title} description={tooltip.desc}/>
                </div>
            )
        }
    }
    
    function getCustomInputClass() {
        if (isRequired && errorChecked) {
            const customClass = hasErrors.error ? "custom-form-control is-invalid" : "custom-form-control is-valid";
            return (customClass);
        } else {
            return ("custom-form-control");
        }
    }

    let mandatoryMark = isRequired ? "(obligatoire)" : "";

    function handleOnChange(event) {
        event.preventDefault();    
        let newValue = event.target.value;
        setValue(newValue);

        if (callbackOnChange)
            callbackOnChange(newValue);
    }
    
    function displayInput() {
        if (type == "textarea")
            return (
                <textarea
                    id={inputId}
                    className={"form-control " + getCustomInputClass()}
                    style={{height: "150px"}}
                    rows="5"
                    value={value}
                    placeholder={placeholder}
                    required={isRequired}
                    disabled={isReadOnly}
                    onBlur={checkErrors}
                    onChange={handleOnChange}
                />           
            )
        else
            return (
                <input 
                    id={inputId}
                    className={"form-control " + getCustomInputClass()}
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    required={isRequired}
                    disabled={isReadOnly}
                    onBlur={checkErrors}
                    onChange={handleOnChange}      
                />        
            )
    }

    function displayLabel() {
        if (label) {
            return (
                <label className="gray-text mb-2" htmlFor={inputId}>{label} {mandatoryMark}</label>
            )
        }
    }

    return (
        <div className="mb-3 has-error has-feedback custom-input-div">
            {displayTooltip()}
            {displayLabel()}
            {displayInput()}                        
            {displayErrorMessage()}
        </div>        
    )
})

export default Input;